import {
    DEFAULT_PLACEHOLDER,
    MoneyTones,
    REPAYMENT_REPORT_FIELDS,
    RepaymentStatuses,
} from '../constants/money.constants';
import {
    formatMoney,
    getMoneyColorClassName,
    toFiniteNumber,
    toneForLedgerRow,
    toneForLedgerType,
} from '../utils/money';

/**
 * Money presentation rules for the whole app: what colour an amount gets, and
 * how it is written out. Both rules are here so no component decides either on
 * its own and the same figure never appears two ways on two screens.
 *
 * **Colour.** Red for a figure the member still owes us — an overdue amount, a
 * pending settlement balance, a negative balance. Green only for what has
 * settled; it must never land on an amount still owed. Neutral for descriptive
 * figures: counts, volume advanced, fees charged, and the "Pending for
 * settlement" state. Colour is never the only signal — every caller also renders
 * a text label.
 *
 * "Owed" is a fact about the member, which only the report can establish. A
 * single ledger row cannot: nothing on it says whether that advance has since
 * been repaid. So rows are toned by direction, not by debt — see
 * {@link MoneyController#toneForLedgerRow}.
 *
 * **Formatting.** Currency symbol, thousands separators, two decimals. Fees are
 * routinely under a dollar, so amounts are never rounded to whole dollars, and
 * they are never abbreviated — ops reads these figures to reconcile real money.
 *
 * The vocabulary lives in `constants/money.constants`. The formatting and
 * colour rules themselves are the pure named exports in `utils/money`; the
 * methods here delegate to them so a page can call `this.formatMoney(...)`
 * while a view that needs one of them imports the function directly rather
 * than instantiating a controller (see `common/signedAmount`). The repayment
 * rules, which read the `getUserReport` payload, live only here.
 *
 * Extend via the Inherit pattern:
 *   class MyController extends Inherit(BaseClass, MoneyController)
 * No constructor and no state: every method is pure, which is also what makes
 * them testable on their own.
 */
class MoneyController {
    /**
     * An exact amount: `$1,234.56`. See {@link formatMoney} in `utils/money`.
     *
     * @param {number|string|null|undefined} value
     * @param {string} [currency='USD'] ISO 4217 code
     * @param {string} [placeholder='—'] rendered for a missing or unparseable value
     * @returns {string}
     */
    formatMoney(value, currency, placeholder) {
        return formatMoney(value, currency, placeholder);
    }

    /**
     * The class name that paints an amount. See {@link getMoneyColorClassName}
     * in `utils/money`.
     *
     * @param {string|null|undefined} tone a {@link MoneyTones} value
     * @returns {string} a class from `styles/money.module.scss`
     */
    getMoneyColorClassName(tone) {
        return getMoneyColorClassName(tone);
    }

    /**
     * Tone for an amount the member owes. Zero is never red — it means there is
     * no debt, which is a neutral fact, not a settled one.
     *
     * @param {number|string|null|undefined} amount
     * @returns {string} a {@link MoneyTones} value — OWED or NEUTRAL, never SETTLED
     */
    toneForOwedAmount(amount) {
        return (toFiniteNumber(amount) ?? 0) > 0 ? MoneyTones.OWED : MoneyTones.NEUTRAL;
    }

    /**
     * Tone for an amount that has settled. Zero is never green — it means
     * nothing has settled, which a member with no financial activity at all
     * must not be told is good news.
     *
     * @param {number|string|null|undefined} amount
     * @returns {string} a {@link MoneyTones} value — SETTLED or NEUTRAL, never OWED
     */
    toneForSettledAmount(amount) {
        return (toFiniteNumber(amount) ?? 0) > 0 ? MoneyTones.SETTLED : MoneyTones.NEUTRAL;
    }

    /**
     * Tone for a ledger row, by the direction its type moved the money — never
     * by whether it is owed. See {@link toneForLedgerType} in `utils/money` for
     * why an advance is neutral rather than red.
     *
     * @param {string|null|undefined} type display type (`advance`, `settlement`, `service_fee`)
     * @returns {string} a {@link MoneyTones} value
     */
    toneForLedgerType(type) {
        return toneForLedgerType(type);
    }

    /**
     * Tone for a consumer-ledger item: the type rule plus the reconciliation
     * flag. See {@link toneForLedgerRow} in `utils/money`.
     *
     * @param {object|null|undefined} record a consumer-ledger item
     * @returns {string} a {@link MoneyTones} value
     */
    toneForLedgerRow(record) {
        return toneForLedgerRow(record);
    }

    /**
     * Whether a `getUserReport` payload carries any of the fields the repayment
     * rules read. A failed fetch reaches the view as `{}` (`fetchConsumerUser`
     * swallows the error and returns an empty object, and the page seeds
     * `consumer: {}`), which is indistinguishable from a real report by shape
     * alone — so it is distinguished by content, and a payload with none of
     * these fields is treated as "no report", never as a report of zeroes.
     *
     * @param {Object|null|undefined} report `getUserReport` payload
     * @returns {boolean}
     */
    hasRepaymentFacts(report) {
        if (!report) return false;
        return REPAYMENT_REPORT_FIELDS.some((field) => report[field] !== undefined);
    }

    /**
     * The member's repayment state. Prefers the token the backend computes; the
     * fallback keeps the card right if an older service is still deployed, and
     * follows the same precedence: overdue beats awaiting, and a member with no
     * advances has nothing to repay.
     *
     * @param {Object|null|undefined} report `getUserReport` payload
     * @returns {string} a {@link RepaymentStatuses} value
     */
    resolveRepaymentStatus(report) {
        const status = report?.repaymentStatus;
        if (status && Object.values(RepaymentStatuses).includes(status)) return status;

        if (report?.isOverdue) return RepaymentStatuses.OVERDUE;
        if (report?.isAwaitingSettlement) return RepaymentStatuses.PENDING_SETTLEMENT;

        const advanced = toFiniteNumber(report?.totalAdvances) ?? 0;
        const volume = toFiniteNumber(report?.advanceAmount) ?? 0;
        return advanced > 0 || volume > 0 ? RepaymentStatuses.PAID : RepaymentStatuses.NO_ACTIVITY;
    }

    /**
     * Label, tone and supporting detail for the Repayment Status card. The label
     * is what makes the state readable without relying on colour.
     *
     * A payload with none of the repayment facts means the report never arrived,
     * which is not the same as a member who never advanced. This card's whole job
     * is to make a claim about the member's money, so it declines to make one
     * rather than asserting "No advances" over a failed fetch.
     *
     * @param {Object|null|undefined} report `getUserReport` payload
     * @returns {{ label: string, tone: string, sublabel: string|null }}
     */
    describeRepaymentStatus(report) {
        if (!this.hasRepaymentFacts(report)) {
            return { label: DEFAULT_PLACEHOLDER, tone: MoneyTones.NEUTRAL, sublabel: null };
        }

        const status = this.resolveRepaymentStatus(report);

        if (status === RepaymentStatuses.OVERDUE) {
            const days = toFiniteNumber(report?.businessDaysPastDue) ?? 0;
            const amount = this.formatMoney(report?.overdueAmount);
            const unit = days === 1 ? 'business day' : 'business days';
            return {
                label: 'Overdue',
                tone: MoneyTones.OWED,
                sublabel: `${amount} · ${days} ${unit} past due`,
            };
        }

        if (status === RepaymentStatuses.PENDING_SETTLEMENT) {
            return { label: 'Pending for settlement', tone: MoneyTones.NEUTRAL, sublabel: null };
        }

        if (status === RepaymentStatuses.PAID) {
            return { label: 'Paid', tone: MoneyTones.SETTLED, sublabel: null };
        }

        return { label: 'No advances', tone: MoneyTones.NEUTRAL, sublabel: null };
    }
}

export default MoneyController;
